'use client';

import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import type { PortableTextBlock } from '@portabletext/types';
import { useMemo } from 'react';

import { LegalPortableText } from '@/components/legal/portable-text';
import { LegalTableOfContents } from '@/components/legal/table-of-contents';
import {
  legalDocumentBodyRevealVariants,
  legalDocumentHeaderItemVariants,
  legalDocumentHeaderRevealVariants,
  legalDocumentMotionInstant,
} from '@/components/shared/motion';
import { buildLegalHeadingNav } from '@/lib/legal-toc';
import { useReducedMotion } from '@/lib/use-reduced-motion';

function resolveVariants(reduce: boolean, animated: Variants): Variants {
  return reduce ? legalDocumentMotionInstant : animated;
}

export function LegalDocumentSections({
  title,
  lastUpdatedLabel,
  copy,
}: {
  title: string;
  lastUpdatedLabel: string | null;
  copy: PortableTextBlock[];
}) {
  const reduce = useReducedMotion();

  const headerReveal = resolveVariants(reduce, legalDocumentHeaderRevealVariants);
  const headerItem = resolveVariants(reduce, legalDocumentHeaderItemVariants);
  const bodyReveal = resolveVariants(reduce, legalDocumentBodyRevealVariants);

  const { toc, idByBlockKey } = useMemo(() => buildLegalHeadingNav(copy), [copy]);

  return (
    <>
      <section className="relative isolate py-12 xl:py-20 mt-[56px] xl:mt-[80px] bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-[1324px]:px-0">
          <motion.div
            className="flex flex-col items-start justify-end h-full w-full gap-3 xl:gap-4"
            variants={headerReveal}
            initial="hidden"
            animate="visible"
          >
            <motion.h1
              variants={headerItem}
              className="text-3xl sm:text-4xl xl:text-5xl text-foreground font-bold"
            >
              {title}
            </motion.h1>
            {lastUpdatedLabel ? (
              <motion.p
                variants={headerItem}
                className="text-sm sm:text-base text-muted-foreground font-light"
              >
                Last updated on {lastUpdatedLabel}
              </motion.p>
            ) : null}
          </motion.div>
        </div>
      </section>
      <section className="relative isolate py-12 xl:py-20 bg-background border-y dark:border-t dark:border-b-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-[1324px]:px-0">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:gap-10 xl:gap-14">
            <motion.div
              className="min-w-0 flex-1 flex flex-col gap-6"
              variants={bodyReveal}
              initial="hidden"
              animate="visible"
            >
              <LegalPortableText value={copy} headingIdByBlockKey={idByBlockKey} />
            </motion.div>

            {toc.length > 0 ? (
              <aside className="hidden lg:block shrink-0 w-[220px] xl:w-[240px]">
                <div className="sticky top-28 max-h-[calc(100vh-9rem)] overflow-y-auto overscroll-contain">
                  <LegalTableOfContents entries={toc} />
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
